import type { SideMenuItem } from '@eleo-rh/core-ui'
import Link from 'components/UI/Link'
import dayjs from 'config/dayjs'
import { permissions } from 'constants/permissions'
import type { UseModulesOutput } from 'hooks/modules'
import type { PermissionCode } from 'shared/permissions'
import type { Settings } from 'shared/types'

interface GetItemsProps {
  tableauURL: string
  i18n: (key: string) => string
  perimeter?: {
    'n-1': boolean
    'n-X': boolean
  }
  settings: Settings
  profileType: string
  modules: UseModulesOutput
  hasPermission: (permission: PermissionCode) => boolean
}

export type LayoutSiderItem = Partial<SideMenuItem> & {
  permissions?: PermissionCode[]
  feature?: string
  enabled?: boolean
  pinnable?: boolean
  children?: LayoutSiderItem[]
}

export interface LayoutSiderItems {
  coreItems: LayoutSiderItem[]
  adminItems: LayoutSiderItem[]
}

function collectPermissions(items: LayoutSiderItem[]): PermissionCode[] {
  return items.flatMap(item => [
    ...(item.permissions || []),
    ...(item.children ? collectPermissions(item.children) : []),
  ])
}

export function getItemPermissions(items: LayoutSiderItem[]): PermissionCode[] {
  return [...new Set(collectPermissions(items))]
}

export const items = ({
  tableauURL,
  i18n,
  perimeter,
  settings,
  profileType,
  modules,
  hasPermission,
}: GetItemsProps): LayoutSiderItems => {
  const { hasSafety, hasWorkplace, hasTraining, isSafetyStandalone } = modules
  const cascadeParam = perimeter?.['n-1'] && perimeter?.['n-X'] ? '&cascade=false' : ''

  const reporting: LayoutSiderItem[] =
    tableauURL && settings?.tableauEnabled
      ? [
          {
            label: <Link to={tableauURL}>{i18n('menu.reporting')}</Link>,
            icon: 'chart-pie',
            permissions: permissions.reporting,
          },
        ]
      : []

  const coreItems: LayoutSiderItem[] = [
    {
      key: 'dashboard',
      label: <Link to="dashboard">{i18n('menu.my_space')}</Link>,
      icon: 'user',
      enabled: profileType === 'collaborator',
    },
    {
      key: 'catalog',
      label: (
        <Link
          to={hasTraining && permissions.catalog.every(hasPermission) ? 'catalog/training' : 'catalog/accreditation'}
        >
          {i18n('menu.catalog')}{' '}
        </Link>
      ),
      icon: 'book-open',
      permissions: isSafetyStandalone
        ? permissions.accreditation
        : [...permissions.catalog, ...permissions.accreditation],
      enabled: hasSafety || hasTraining,
    },
    {
      key: 'needs-summary',
      label: <Link to="needs-summary">{i18n('menu.collection_need')}</Link>,
      icon: 'clipboard-list',
      permissions: permissions.needsSummary,
      enabled: hasTraining,
    },
    {
      key: 'sessions',
      label: <Link to="sessions">{i18n('menu.collection_sessions')}</Link>,
      icon: 'calendar-check',
      permissions: permissions.sessionView,
      enabled: hasTraining,
    },
    {
      key: `planning?date=${dayjs(new Date()).format('YYYY-MM-DD')}`,
      label: (
        <Link to={`planning?date=${dayjs(new Date()).format('YYYY-MM-DD')}`}>
          {i18n('menu.collection_reservations')}
        </Link>
      ),
      icon: 'calendar-day',
      permissions: permissions.roomBookingCanView,
      enabled: hasWorkplace,
    },
    {
      key: 'training-plan',
      label: <Link to="training-plan">{i18n('menu.training_plan')}</Link>,
      icon: 'chart-diagram',
      permissions: permissions.trainingPlan,
      enabled: hasTraining,
    },
    {
      key: 'accreditations?user.enabled=true',
      label: <Link to="accreditations?user.enabled=true">{i18n('menu.accreditation_board.hse')}</Link>,
      icon: 'chart-network',
      permissions: permissions.accreditationBoard,
      enabled: hasSafety,
    },
    {
      key: 'renewable-trainings?user.enabled=true',
      label: <Link to="renewable-trainings?user.enabled=true">{i18n('menu.accreditation_board')}</Link>,
      icon: 'chart-network',
      permissions: permissions.renewableTrainingBoard,
      enabled: hasTraining,
    },
    {
      key: 'collectives',
      label: <Link to="collectives">{i18n('menu.collectives_management')}</Link>,
      icon: 'users',
      permissions: permissions.collectives,
      enabled: hasTraining,
    },
    {
      key: `my-perimeter?enabled=true${cascadeParam}`,
      label: <Link to={`my-perimeter?enabled=true${cascadeParam}`}>{i18n('menu.perimeter')}</Link>,
      icon: 'user-group',
      permissions: permissions.myPerimeter,
      feature: 'myPerimeter',
    },
    {
      key: `training-history?${cascadeParam}`,
      label: <Link to={`training-history?${cascadeParam}`}>{i18n('menu.training_history')}</Link>,
      icon: 'clock-rotate-left',
      permissions: permissions.trainingHistory,
      enabled: hasTraining,
    },
    ...reporting,
  ]

  const adminItems: LayoutSiderItem[] = [
    {
      key: 'menu.admin_settings',
      label: i18n('menu.admin_settings'),
      children: [
        {
          key: 'admin/modules&applications',
          label: <Link to="admin/modules&applications">{i18n('menu.admin_modules&apps')}</Link>,
          permissions: permissions.adminSettings,
        },
        {
          key: 'admin/settings',
          label: <Link to="admin/settings">{i18n('menu.admin_settings_global')}</Link>,
          permissions: permissions.adminSettings,
        },
        {
          key: 'admin/settings/organizations',
          label: <Link to="admin/settings/organizations">{i18n('menu.admin-companies-organizations')}</Link>,
          permissions: permissions.adminUsersOrganizations,
        },
        {
          key: 'admin/session/organization-types',
          label: <Link to="admin/session/organization-types">{i18n('menu.admin-company-types')}</Link>,
          permissions: permissions.adminUsersOrganizations,
        },
        {
          key: 'admin/settings/collaborators',
          label: <Link to="admin/settings/collaborators">{i18n('admin.crud.collaborators.view')}</Link>,
          permissions: permissions.adminCollaborators,
        },
        {
          key: 'admin/profiles',
          label: <Link to="admin/profiles">{i18n('menu.admin_settings_profiles')}</Link>,
          permissions: permissions.adminProfiles,
        },
        {
          key: 'admin/sub-profiles',
          label: <Link to="admin/sub-profiles">{i18n('menu.admin_settings_subprofiles')}</Link>,
          permissions: permissions.adminSubProfiles,
        },
        {
          key: 'admin/populations',
          label: <Link to="admin/populations">{i18n('menu.admin_populations')}</Link>,
          permissions: permissions.adminPopulations,
        },
        {
          key: 'admin/perimeter',
          label: (
            <Link
              to={
                permissions.adminPerimetersTraining.some(hasPermission)
                  ? 'admin/perimeter/training'
                  : 'admin/perimeter/accreditation'
              }
            >
              {i18n('menu.admin-perimeter')}
            </Link>
          ),
          permissions: [...permissions.adminPerimetersTraining, ...permissions.adminPerimetersAccreditation],
        },
        {
          key: 'admin/doc-templates',
          label: <Link to="admin/doc-templates">{i18n('menu.admin_doc_templates')}</Link>,
          permissions: permissions.adminDocTemplates,
        },
      ],
    },
    {
      key: 'menu.admin-training-catalog',
      label: i18n('menu.admin-training-catalog'),
      permissions: [...permissions.adminTrainings, ...permissions.trainingManagement],
      children: [
        {
          key: 'admin/training-management',
          label: <Link to="training-management">{i18n('menu.admin-training-list')}</Link>,
          permissions: permissions.trainingManagement,
          pinnable: true,
        },
        {
          key: 'admin/training/super-categories',
          label: <Link to="admin/training/super-categories">{i18n('menu.admin_training_super_categories')}</Link>,
          permissions: permissions.adminTrainings,
        },
        {
          key: 'admin/training/categories',
          label: <Link to="admin/training/categories">{i18n('menu.admin_training_categories')}</Link>,
          permissions: permissions.adminTrainings,
        },
        {
          key: 'admin/training/themes',
          label: <Link to="admin/training/themes">{i18n('menu.admin_training_themes')}</Link>,
          permissions: permissions.adminTrainings,
        },
        {
          key: 'admin/teaching-methods',
          label: <Link to="admin/teaching-methods">{i18n('menu.admin_teaching_methods')}</Link>,
          permissions: permissions.adminTrainings,
          enabled: hasTraining,
        },
        {
          key: 'admin/training/trainers',
          label: <Link to="admin/training/trainers/internal">{i18n('admin_trainers')}</Link>,
          permissions: permissions.adminTrainings,
          enabled: hasTraining || hasWorkplace,
        },
        {
          key: 'admin/training/plans',
          label: <Link to="admin/training/plans">{i18n('menu.admin_training_plans')}</Link>,
          permissions: permissions.adminTrainings,
          enabled: hasTraining,
        },
        {
          key: 'admin/training/budgets',
          label: <Link to="admin/training/budgets">{i18n('admin.crud.budgets.list')}</Link>,
          permissions: permissions.adminTrainings,
          enabled: hasTraining,
        },
      ],
    },
    {
      key: 'menu.admin_accreditation_management',
      label: i18n('menu.admin_accreditation_management'),
      permissions: permissions.adminAccreditationManagement,
      enabled: hasSafety,
      children: [
        {
          key: 'admin/accreditation/accreditations',
          label: <Link to="admin/accreditation/accreditations">{i18n('menu.admin_accreditations')}</Link>,
          pinnable: true,
        },
        {
          key: 'admin/accreditation/domains',
          label: <Link to="admin/accreditation/domains">{i18n('menu.admin_accreditation_domains')}</Link>,
        },
        {
          key: 'admin/accreditation/prerequisites',
          label: <Link to="admin/accreditation/prerequisites">{i18n('menu.admin_accreditation_prerequisites')}</Link>,
        },
        {
          key: 'admin/accreditation/application-fields',
          label: (
            <Link to="admin/accreditation/application-fields">
              {i18n('menu.admin_accreditation_application_fields')}
            </Link>
          ),
        },
        {
          key: 'admin/accreditation/trigger-mails',
          label: (
            <Link to="admin/accreditation/trigger-mails">{i18n('menu.admin_accreditation_mail_configuration')}</Link>
          ),
        },
        {
          key: 'admin/accreditation/signings',
          label: <Link to="admin/accreditation/signings">{i18n('menu.admin_accreditation_signings')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin_wish',
      label: i18n('menu.admin-training-wishes'),
      children: [
        {
          key: 'admin/vintages',
          label: <Link to="admin/vintages">{i18n('menu.admin_vintages')}</Link>,
          permissions: [...permissions.adminVintages, ...permissions.adminWishes],
        },
        {
          key: 'admin/training-wish/priorities',
          label: <Link to="admin/training-wish/priorities">{i18n('menu.admin.priorities')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'admin/training-wish/decision-reasons',
          label: (
            <Link
              to={
                hasPermission('ADMIN_WISH')
                  ? 'admin/training-wish/decision-reasons/arbitration-refusal'
                  : 'admin/training-wish/decision-reasons/request-cancellation'
              }
            >
              {i18n('menu.admin_decision_reasons')}
            </Link>
          ),
          permissions: [...permissions.adminTrainingPlan, ...permissions.adminWishes],
          enabled: hasTraining,
        },
        {
          key: 'admin/training/projects',
          label: <Link to="admin/training/projects">{i18n('menu.admin_training_projects')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'admin/training-wish/reasons',
          label: <Link to="admin/training-wish/reasons">{i18n('menu.admin_wish_reasons')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'admin/training-wish/types',
          label: <Link to="admin/training-wish/types">{i18n('menu.admin_wish_types')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'admin/training-wish/orientations',
          label: <Link to="admin/training-wish/orientations">{i18n('menu.admin_wish_orientations')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'admin/training-wish/perimeters',
          label: <Link to="admin/training-wish/perimeters">{i18n('menu.admin_wish_perimeters')}</Link>,
          permissions: permissions.adminWishes,
          enabled: hasTraining,
        },
        {
          key: 'deleted-wishes',
          label: <Link to="deleted-wishes">{i18n('menu.deleted_wishes')}</Link>,
          permissions: permissions.deletedWishes,
          enabled: hasTraining,
        },
      ],
    },
    {
      key: 'menu.admin_session',
      label: i18n('menu.admin_session'),
      permissions: permissions.adminSessionManagement,
      enabled: hasTraining || hasWorkplace,
      children: [
        {
          key: 'admin/surveys',
          label: <Link to="admin/surveys">{i18n('label_surveys')}</Link>,
          permissions: permissions.adminSurveys,
          enabled: hasTraining,
        },
        {
          key: 'admin/session/types',
          label: <Link to="admin/session/types">{i18n('menu.admin_session_types')}</Link>,
          permissions: permissions.adminSessionTypes,
        },
        {
          key: 'admin/session/training-spaces',
          label: (
            <Link
              to={`admin/session/training-spaces/${hasPermission('SESSION_ADMIN_TRAINING_ROOMS') ? 'rooms' : 'places'}`}
            >
              {i18n('menu.admin-training-spaces')}
            </Link>
          ),
          permissions: [...permissions.adminSessionLocations, ...permissions.adminSessionTrainingRooms],
        },
        {
          key: 'admin/session/logistics',
          label: <Link to="admin/session/logistics/room-layouts">{i18n('menu.admin-logistics')}</Link>,
        },
        {
          key: 'admin/session/costs',
          label: <Link to="admin/session/costs">{i18n('label.costs')}</Link>,
          permissions: permissions.adminSessionCosts,
          enabled: hasTraining,
        },
        {
          key: 'admin/animation-locales',
          label: <Link to="admin/animation-locales">{i18n('menu.admin_animation_locales')}</Link>,
          permissions: permissions.adminAnimationLocales,
          enabled: hasTraining,
        },
        {
          key: 'admin/session/reasons',
          label: <Link to="admin/session/reasons/cancellation">{i18n('menu.admin-session-reasons')}</Link>,
          permissions: permissions.adminSessionManagement,
          enabled: hasTraining,
        },
        {
          key: 'admin/session/task-management',
          label: <Link to="admin/session/task-management/single-tasks">{i18n('menu.admin_session_tasks')}</Link>,
          permissions: permissions.adminSessionTasks,
          enabled: hasTraining,
        },
        {
          key: 'admin/session/custom-fields',
          label: <Link to="admin/session/custom-fields">{i18n('session.custom-fields.title')}</Link>,
          permissions: permissions.adminCustomFields,
          enabled: hasTraining,
        },
        {
          key: 'admin/session/participant-information',
          label: <Link to="admin/session/participant-information">{i18n('participant_information')}</Link>,
          permissions: permissions.adminSessionParticipantInformation,
          enabled: hasTraining,
        },
      ],
    },
    {
      key: 'menu.admin-skills',
      label: i18n('menu.admin-skills'),
      permissions: permissions.adminMatrix,
      enabled: hasTraining,
      children: [
        {
          key: 'admin/matrix/level-templates',
          label: <Link to="admin/matrix/level-templates">{i18n('admin_matrix_level_templates')}</Link>,
        },
        {
          key: 'admin/matrix/skill-types',
          label: <Link to="admin/matrix/skill-types">{i18n('admin_matrix_skill_types')}</Link>,
        },
        {
          key: 'admin/matrix/skills',
          label: <Link to="admin/matrix/skills">{i18n('admin_matrix_skills')}</Link>,
        },
        {
          key: 'admin/matrix/job-families',
          label: <Link to="admin/matrix/job-families">{i18n('admin_matrix_job_family')}</Link>,
        },
        {
          key: 'admin/matrix/jobs',
          label: <Link to="admin/matrix/jobs">{i18n('admin_matrix_jobs')}</Link>,
        },
      ],
    },
    {
      key: 'menu.admin-emails',
      label: i18n('menu.admin-emails'),
      permissions: [...permissions.adminEmailTemplates, ...permissions.adminEmailScheduled],
      enabled: hasTraining || hasSafety,
      children: [
        {
          key: 'admin/email-templates',
          label: <Link to="admin/email-templates">{i18n('menu.admin_email_templates')}</Link>,
          permissions: permissions.adminEmailTemplates,
        },
        {
          key: 'admin/email-scheduled',
          label: <Link to="admin/email-scheduled">{i18n('menu.admin_email_scheduled')}</Link>,
          permissions: permissions.adminEmailScheduled,
        },
        {
          key: 'admin/email-sent',
          label: <Link to="admin/email-sent">{i18n('menu.admin_email_sent')}</Link>,
          permissions: permissions.adminEmailSent,
        },
      ],
    },
    {
      key: 'menu.admin-import-export',
      label: i18n('menu.admin-import-export'),
      permissions: permissions.adminImports,
      children: [
        {
          key: 'admin/import',
          label: <Link to="admin/import">{i18n('menu.admin-import')}</Link>,
        },
        {
          key: 'admin/configurable-exports',
          label: <Link to="admin/configurable-exports">{i18n('label.configurable_exports')}</Link>,
          permissions: permissions.configurableExports,
        },
        {
          key: 'admin/automations',
          label: <Link to="admin/automations">{i18n('label.automations')}</Link>,
          permissions: permissions.adminAutomations,
        },
      ],
    },
  ]

  return { coreItems, adminItems }
}
